import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateAvatar: (avatar) => api.put('/auth/me/avatar', { avatar }),
  updateProfile: (data) => api.put('/auth/me', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Locations API
export const locationsAPI = {
  getAll: (params) => api.get('/locations', { params }),
  getById: (id) => api.get(`/locations/${id}`),
  getBusyDates: (id) => api.get(`/locations/${id}/busy-dates`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  getByUser: (userId) => api.get(`/locations/user/${userId}`),
  getFacilities: () => api.get('/locations/facilities/all'),
  getRandomFeatured: (limit = 4) => api.get('/locations/random/featured', { params: { limit } }),
};

// Organized events API
export const eventsAPI = {
  listAll: () => api.get('/events'),
  listByLocation: (locationId) => api.get(`/events/location/${locationId}`),
  listMine: () => api.get('/events/mine'),
  getById: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
};

// Favorites API
export const favoritesAPI = {
  list: () => api.get('/favorites'),
  listIds: () => api.get('/favorites/ids'),
  add: (locationId) => api.post(`/favorites/${locationId}`),
  remove: (locationId) => api.delete(`/favorites/${locationId}`),
};

// Owner availability blocks (maintenance / private events)
export const ownerBlocksAPI = {
  list: (locationId) => api.get(`/owner-blocks/location/${locationId}`),
  publicList: (locationId) => api.get(`/owner-blocks/public/location/${locationId}`),
  create: (data) => api.post('/owner-blocks', data),
  remove: (id) => api.delete(`/owner-blocks/${id}`),
};

// Bookings API
export const bookingsAPI = {
  create: (data) => api.post('/bookings', data),
  getByUser: (userId) => api.get(`/bookings/user/${userId}`),
  getByLocation: (locationId) => api.get(`/bookings/location/${locationId}`),
  getById: (id) => api.get(`/bookings/${id}`),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status`, null, { params: { status } }),
  // Host approval flow
  getHostPendingApprovals: () => api.get('/bookings/host/pending-approvals'),
  approve: (id) => api.put(`/bookings/${id}/approve`),
  reject: (id, reason = '') => api.put(`/bookings/${id}/reject`, { reason }),
};

// Payments / Refunds API
export const paymentsAPI = {
  getRefundPreview: (bookingId) => api.get(`/payments/bookings/${bookingId}/refund-preview`),
  cancelBooking: (bookingId) => api.post(`/payments/bookings/${bookingId}/cancel`),
  getAccountStatus: () => api.get('/payments/connect/account-status'),
  createConnectAccount: (data) => api.post('/payments/connect/create-account', data),
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout-status/${sessionId}`),
};

// Admin API
export const adminAPI = {
  getOverview: () => api.get('/admin/overview'),
  listBookings: (params = {}) => api.get('/admin/bookings', { params }),
  listUsers: () => api.get('/admin/users'),
  listLocations: () => api.get('/admin/locations'),
  approveLocation: (id) => api.patch(`/admin/locations/${id}/approve`),
  suspendLocation: (id) => api.patch(`/admin/locations/${id}/suspend`),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  listContactMessages: (params = {}) => api.get('/admin/contact-messages', { params }),
  unreadContactCount: () => api.get('/admin/contact-messages/unread-count'),
  markContactRead: (id, read = true) => api.patch(`/admin/contact-messages/${id}/read`, null, { params: { read } }),
  deleteContactMessage: (id) => api.delete(`/admin/contact-messages/${id}`),
  listInterest: () => api.get('/admin/interest'),
  deleteInterest: (id) => api.delete(`/admin/interest/${id}`),
  listTermsConsents: () => api.get('/admin/terms-consents'),
  listUserBookings: (userId) => api.get(`/admin/users/${userId}/bookings`),
  resendEmail: (data) => api.post('/admin/resend-email', data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  syncUserStripe: (userId) => api.post(`/admin/users/${userId}/sync-stripe`),
};

// Public site settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
};

// Public contact form API
export const contactAPI = {
  submit: (data) => api.post('/contact-messages', data),
};

// Interest signup for not-yet-bookable locations
export const interestAPI = {
  signup: (data) => api.post('/interest', data),
};

// Locations management API (for host owners)
export const locationsManagementAPI = {
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  toggleActive: (id) => api.patch(`/locations/${id}/toggle-active`),
  getStats: (id) => api.get(`/locations/${id}/stats`),
};

// Reviews API
export const reviewsAPI = {
  createGuestReview: (data) => api.post('/reviews/guest', data),
  createHostReview: (data) => api.post('/reviews/host', data),
  getLocationReviews: (locationId) => api.get(`/reviews/location/${locationId}`),
  getGuestReviews: (guestId) => api.get(`/reviews/guest/${guestId}`),
  checkPendingReviews: (bookingId) => api.get(`/reviews/booking/${bookingId}/pending`),
  getBookingReviews: (bookingId) => api.get(`/reviews/booking/${bookingId}`),
  getMyPending: () => api.get('/reviews/me/pending'),
};

export default api;
